import { AuthUserService } from '../auth/auth-user.service';
import { AngularFirestore } from '@angular/fire/firestore';
import { DocumentReference } from '@firebase/firestore-types';
import { Injectable } from '@angular/core';
import { Item, Items } from '../items/item.model';
import { NfmbDataService } from '../nfmb-data.service';
import { Observable, BehaviorSubject, combineLatest, of } from 'rxjs';
import { switchMap, first, shareReplay } from 'rxjs/operators';
import { Profiles } from '../profiles/profile.model';

@Injectable({
  providedIn: 'root'
})
export class ItemService extends NfmbDataService {

  private categoryFilter$: BehaviorSubject<string|null>;

  constructor(
    afs: AngularFirestore,
    authUserService: AuthUserService
  ) {
    super(afs, authUserService, 'items');
    console.log('ItemService constructor');
    this.categoryFilter$ = new BehaviorSubject(null);

    this.permissions = {
      edit: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN,
        Profiles.Role.EDITOR
      ],
      delete: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN
      ],
      read: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN,
        Profiles.Role.EDITOR,
        Profiles.Role.READER
      ],
      viewAll: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN
      ]
    };
  }

  get data$(): Observable<any> {
    return combineLatest([
      this.categoryFilter$,
      this.authUserService.currentProfile$,
    ]).pipe(
      switchMap( ([category, profile]) => {
        console.log('Item Stream emit new value for following category and profile: ', { category, profile });
        if (profile) {
          return this.afs.collection<Item>(this.collectionPath,
            ref => {
              /**
               * Add organizationId as default group
               */
              profile.groups.push(profile.organizationId);

              /**
               * If current profile has permissions to view all, filter by groups and target type is disabled
               */
              const roles = (profile && profile.roles) ? profile.roles : [];
              const canViewAll = this.haveRolesPermissionsTo(roles, 'viewAll');

              let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
              if (!canViewAll) {
                query = query.where(Items.IndexField.GROUPS, 'array-contains-any', profile.groups);
              }
              query = query.where(Items.IndexField.ORGANIZATION_ID, '==', profile.organizationId);
              if (!canViewAll) {
                query = query.where(`${Items.IndexField.TARGET_PROFILE_TYPES}.${profile.type}`, '==', true);
              }
              if (category) { query = query.where(Items.IndexField.CATEGORY, '==', category); }
              query = query.orderBy(Items.IndexField.PUBLISHED_AT, 'desc');
              return query;
            }
          ).valueChanges({ idField: 'id' });
        } else {
          console.log('return empty items list');
          return of([]);
        }
      }),
      shareReplay(1)
    );
  }

  async push(item: any): Promise<DocumentReference> {
    const currentProfile = await this.authUserService.currentProfile$.pipe(first()).toPromise();
    if (!(currentProfile && currentProfile.organizationId)) {
      return Promise.reject('user not authorized');
    }
    const timestamp = this.timestamp;
    if (item.groups && item.groups.length === 0) {
      item.groups = [currentProfile.organizationId];
    }
    return this.afs.collection<Item>(this.collectionPath).add({
      ...item,
      organizationId: currentProfile.organizationId,
      createdAt: timestamp,
      modifiedAt: timestamp
    });
  }

  async update(id: string, data: any): Promise<void> {
    const currentProfile = await this.authUserService.currentProfile$.pipe(first()).toPromise();
    data.modifiedAt = this.timestamp;
    if (data.groups && data.groups.length === 0) {
      data.groups = [currentProfile.organizationId];
    }

    return this.afs.doc<any>(`${this.collectionPath}/${id}`).update(data);
  }

  public filterByCategory(category: string): void {
    return this.categoryFilter$.next(category);
  }

  public resetPublicFilters(): void {
    return this.categoryFilter$.next(null);
  }
}
